import { i18n, prefixes, UI_LANGUAGE_OPTIONS, type UILanguageCode, useTypedTranslation } from "@/locales";
import { type ReactNode, useEffect, useState } from 'react';
import { I18nProvider } from 'react-aria';
import { getLanguageDirection } from '@/utils/language-utils';
import { ButtonGroup, ButtonGroupItem } from '@/components/_untitled/base/button-group/button-group';
import { NavItemBase } from '@/components/_untitled/application/app-navigation/base-components/nav-item';
import { ChevronDown, Translate01 } from '@untitledui/icons';
import { Dot } from '@/components/_untitled/foundations/dot-icon';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/api/authApi';

interface LanguageProps {
    className?: string;
    children?: ReactNode;
}

interface LanguageToggleProps {
    variant?: 'sidebar' | 'header';
}

export const LanguageProvider = ({ children }: LanguageProps) => {
    const direction = getLanguageDirection(i18n.language);

    useEffect(() => {
        if (i18n.language) {
            document.documentElement.dir = direction;
            document.documentElement.lang = i18n.language.split('-')[0];
        }
    }, [direction]);

    return (
        <I18nProvider locale={ i18n.language }>
            { children }
        </I18nProvider>
    );
};

export const LanguageToggle = ({ variant = 'sidebar' }: LanguageToggleProps) => {
    const [tHeader, tNavigation] = useTypedTranslation(prefixes('header', 'navigation'));
    const [isOpen, setIsOpen] = useState(false);
    const { isAuthenticated, refetchUser } = useAuth();

    const handleLanguageChange = async (langCode: UILanguageCode) => {
        i18n.changeLanguage(langCode);
        setIsOpen(false);

        // Save language preference to backend if user is logged in
        if (isAuthenticated) {
            try {
                await authApi.updatePreferences({ language: langCode });
                await refetchUser();
            } catch (error) {
                console.error('Failed to save language preference:', error);
            }
        }
    };

    // Normalize language code (i18n.language might be 'en-US', 'he-IL', etc.)
    const normalizedLang = i18n.language.split('-')[0];
    const currentLanguage = UI_LANGUAGE_OPTIONS.find(lang => lang.code === normalizedLang) || UI_LANGUAGE_OPTIONS[0];

    if (variant === 'header') {
        return (
            <div className="relative">
                <button
                    className="flex items-center gap-1 text-white text-sm cursor-pointer"
                    onClick={ () => setIsOpen(!isOpen) }
                    aria-label={ tHeader('languageSelector') }
                >
                    <span>{ currentLanguage.label }</span>
                    <ChevronDown className="size-5 text-white"/>
                </button>

                { isOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            onClick={ () => setIsOpen(false) }
                        />
                        <div
                            className="absolute top-full mt-3 end-0 bg-white rounded-lg shadow-lg border border-border-secondary z-50 min-w-[150px]">
                            { UI_LANGUAGE_OPTIONS.map((lang) => (
                                <button
                                    key={ lang.code }
                                    className="w-full px-4 py-2 text-start text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg cursor-pointer focus:outline-none"
                                    onClick={ () => handleLanguageChange(lang.code as UILanguageCode) }
                                >
                                    <span className={ normalizedLang === lang.code ? 'font-semibold' : '' }>
                                        { lang.label }
                                    </span>
                                </button>
                            )) }
                        </div>
                    </>
                ) }
            </div>
        );
    }

    return (
        <details
            className="appearance-none py-0.5"
            open={ isOpen }
            onToggle={ (e) => setIsOpen(e.currentTarget.open) }
        >
            <NavItemBase
                icon={ Translate01 }
                type="collapsible"
            >
                { tNavigation('languageSelection') }
            </NavItemBase>

            <dd className="px-4 py-2">
                <ButtonGroup className="w-full" size="xs">
                    { UI_LANGUAGE_OPTIONS.map((lang) => (
                        <ButtonGroupItem
                            hideSelected={ true }
                            key={ lang.code }
                            className="flex-1 justify-center"
                            iconLeading={ normalizedLang === lang.code ?
                                <Dot className="mx-0.75 size-2 text-fg-success-secondary"/> :
                                undefined
                            }
                            onPress={ () => handleLanguageChange(lang.code as UILanguageCode) }
                        >
                            { lang.label }
                        </ButtonGroupItem>
                    )) }
                </ButtonGroup>
            </dd>
        </details>
    );
};